import React from 'react';
import { Box, IconButton, Typography, Paper } from '@mui/material';
import {
  Close as CloseIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  TableChart as ExcelIcon,
  FolderZip as ZipIcon,
} from '@mui/icons-material';

interface FilePreviewProps {
  file: File;
  onRemove: () => void;
}

const FilePreview: React.FC<FilePreviewProps> = ({ file, onRemove }) => {
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = () => {
    const type = file.type.toLowerCase();
    const name = file.name.toLowerCase();

    if (type.startsWith('image/')) return <ImageIcon sx={{ color: '#4caf50' }} />;
    if (type === 'application/pdf' || name.endsWith('.pdf')) return <PdfIcon sx={{ color: '#f44336' }} />;
    if (type.includes('word') || name.endsWith('.doc') || name.endsWith('.docx')) return <DocIcon sx={{ color: '#2196f3' }} />;
    if (type.includes('excel') || type.includes('spreadsheet') || name.endsWith('.xls') || name.endsWith('.xlsx')) return <ExcelIcon sx={{ color: '#4caf50' }} />;
    if (type.includes('zip') || name.endsWith('.zip')) return <ZipIcon sx={{ color: '#ff9800' }} />;
    return <FileIcon sx={{ color: 'text.secondary' }} />;
  };

  const isImage = file.type.startsWith('image/');
  const previewUrl = isImage ? URL.createObjectURL(file) : null;

  return (
    <Paper
      elevation={1}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        borderRadius: 1,
        backgroundColor: 'background.default',
        maxWidth: 250,
      }}
    >
      {/* Remove button */}
      <IconButton
        size="small"
        onClick={onRemove}
        sx={{
          position: 'absolute',
          top: -8,
          right: -8,
          backgroundColor: 'background.paper',
          boxShadow: 1,
          '&:hover': { backgroundColor: '#ffebee' },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      {/* Preview or icon */}
      {isImage && previewUrl ? (
        <Box
          component="img"
          src={previewUrl}
          alt={file.name}
          sx={{
            width: 48,
            height: 48,
            objectFit: 'cover',
            borderRadius: 0.5,
          }}
        />
      ) : (
        <Box
          sx={{
            width: 48,
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'divider',
            borderRadius: 0.5,
          }}
        >
          {getFileIcon()}
        </Box>
      )}

      {/* File info */}
      <Box sx={{ flex: 1, minWidth: 0, pr: 2 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {file.name}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {formatFileSize(file.size)}
        </Typography>
      </Box>
    </Paper>
  );
};

export default FilePreview;
